<template>
  <div class="rebook-travel">
    <div class="page-wrapper">
      <BackLink
        :text="$t('afterSale.rebook.backToTickets')"
        :route-to="backLinkRoute"
      />
      <div class="rebook-travel__header">
        <h1 class="rebook-travel__title title-medium">
          {{ $t('afterSale.rebook.title') }}
        </h1>
      </div>

      <div class="rebook-travel__content">
        <div
          v-if="isLoading"
          class="rebook-travel__loading"
        >
          {{ $t('afterSale.rebook.loading') }}
        </div>
        <template v-else>
          <div class="rebook-travel__cards">
            <div
              v-if="outboundSegment && !inboundSegment"
              class="rebook-travel__card card"
            >
              <AfterSalesTravel
                :segment="outboundSegment"
                direction-label="Outbound"
                :show-checkbox="false"
                :has-passengers="false"
                :is-rebooking="true"
                :passengers="booking?.passengers || []"
                class="rebook-travel__segment"
              />
              <div class="rebook-travel__card-cta">
                <button
                  class="rebook-travel__card-button button button--reset"
                  @click="openModal('outbound')"
                  :disabled="!canRebookOutbound"
                >
                  {{ $t('afterSale.rebook.modalTitles.outbound') }}
                  <svg class="trailing__chevron">
                    <use :xlink:href="`#${chevron}`" />
                  </svg>
                </button>
              </div>
            </div>
            <!-- <div
              v-if="inboundSegment"
              class="rebook-travel__card card"
            >
              <AfterSalesTravel
                :segment="inboundSegment"
                direction-label="Return"
                :show-checkbox="false"
                :has-passengers="false"
                :is-rebooking="true"
                :passengers="booking?.passengers || []"
                class="rebook-travel__segment"
              />
              <div class="rebook-travel__card-cta">
                <button
                  class="rebook-travel__card-button button button--reset"
                  @click="openModal('inbound')"
                  :disabled="!canRebookInbound"
                >
                  {{ $t('afterSale.rebook.changeReturn') }}
                  <svg class="trailing__chevron">
                    <use :xlink:href="`#${chevron}`" />
                  </svg>
                </button>
              </div>
            </div> -->

            <div
              v-if="outboundSegment && inboundSegment"
              class="rebook-travel__card card"
            >
              <div class="rebook-travel__card-header">
                <span class="after-sales-travel__station">
                  {{ $t('afterSale.rebook.changeEntireTrip') }}
                </span>
              </div>
              <div class="rebook-travel__card-cta">
                <button
                  class="rebook-travel__card-button button button--reset"
                  @click="openModal('entire')"
                  :disabled="!canRebookEntire"
                >
                  {{ $t('afterSale.rebook.changeEntireTrip') }}
                  <svg class="trailing__chevron">
                    <use :xlink:href="`#${chevron}`" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <Modal
      v-if="showRebookModal"
      :title="$t(`afterSale.rebook.modalTitles.${currentDirection}`)"
      :show="showRebookModal"
      :showOverflow="true"
      @close="closeModal"
    >
      <div
        v-if="errorMessage"
        class="error-message"
      >
        {{ errorMessage }}
      </div>
      <RebookSearch
        v-else
        :direction="currentDirection"
        :disabled="!canRebook"
        :booking-number="booking?.booking_number || ''"
        @search="handleSearch"
      />
    </Modal>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useUserStore } from '@/stores/userStore'
  import { useAuthStore } from '@/stores/oauthStore'
  import { useI18n } from 'vue-i18n'
  import AfterSalesTravel from '@/components/MyPage/AfterSale/AfterSalesTravel.vue'
  import BackLink from '@/components/BaseComponents/BackLink.vue'
  import Button from '@/components/BaseComponents/Button.vue'
  import Modal from '@/components/BaseComponents/Modal/Modal.vue'
  import RebookSearch from '@/components/MyPage/AfterSale/RebookSearch.vue'
  import chevron from '@/assets/icons/chevron.svg'

  export default defineComponent({
    name: 'AfterSaleRebook',
    components: {
      AfterSalesTravel,
      BackLink,
      Button,
      Modal,
      RebookSearch,
    },
    props: {
      bookingNumber: {
        type: String,
        required: true,
      },
    },

    setup(props) {
      const { t } = useI18n()
      const router = useRouter()
      const bookingStore = useBookingStore()
      const journeyStore = useJourneyStore()
      const userStore = useUserStore()
      const authStore = useAuthStore()

      const backLinkRoute = computed(() => ({
        name: 'ticketPage',
        params: { bookingNumber: props.bookingNumber },
      }))
      const showRebookModal = ref(false)
      const errorMessage = ref('')
      const currentDirection = ref<'outbound' | 'inbound' | 'entire'>(
        'outbound'
      )
      const isLoading = ref(true)

      onMounted(async () => {
        const bookingNumber = router.currentRoute.value.params
          .bookingNumber as string
        if (!bookingNumber) {
          console.error('No booking number provided')
          errorMessage.value = 'No booking number provided'
          isLoading.value = false
          return
        }

        try {
          if (
            !bookingStore.bookingData ||
            bookingStore.bookingData.booking_number !== bookingNumber
          ) {
            await bookingStore.fetchBookingData(bookingNumber)
          }

          await bookingStore.fetchAfterSalesProfile(bookingNumber)
        } catch (error) {
          console.error('Error fetching data:', error)
          errorMessage.value = 'Could not load booking data'
        } finally {
          isLoading.value = false
        }
      })

      const booking = computed(() => {
        return bookingStore.bookingData
      })

      const outboundSegment = computed(() => {
        if (!booking.value?.outbound_booking_tariff_segments?.length)
          return null
          
        const segments = booking.value.outbound_booking_tariff_segments
        const firstSegment = segments[0]
        const lastSegment = segments[segments.length - 1]

        return {
          ...firstSegment,
          segments: segments,
          departure_station: {
            ...firstSegment.departure_station,
            connected_stations: firstSegment.departure_station.connected_stations || [],
            meta_data: firstSegment.departure_station.meta_data || [],
            timezone: firstSegment.departure_station.timezone || 'Europe/Oslo',
            country_code: firstSegment.departure_station.country_code || 'NOR',
            short_code: firstSegment.departure_station.short_code || '',
            name: firstSegment.departure_station.name || '',
            _u_i_c_station_code: firstSegment.departure_station._u_i_c_station_code || '',
          },
          arrival_station: {
            ...lastSegment.arrival_station,
            connected_stations: lastSegment.arrival_station.connected_stations || [],
            meta_data: lastSegment.arrival_station.meta_data || [],
            timezone: lastSegment.arrival_station.timezone || 'Europe/Oslo',
            country_code: lastSegment.arrival_station.country_code || 'NOR',
            short_code: lastSegment.arrival_station.short_code || '',
            name: lastSegment.arrival_station.name || '',
            _u_i_c_station_code: lastSegment.arrival_station._u_i_c_station_code || '',
          },
          booking_journey_segments: firstSegment.booking_journey_segments || [],
          required_products: firstSegment.required_products || [],
          additional_products: firstSegment.additional_products || [],
          fees: firstSegment.fees || [],
          travel_info: firstSegment.travel_info || [],
          travel_info_extended: firstSegment.travel_info_extended || [],
          validity_weekdays: firstSegment.validity_weekdays || '',
          tariff_group_code: firstSegment.tariff_group_code || '',
          uuid: firstSegment.uuid || '',
          travel_date: firstSegment.travel_date || '',
          sequence_number: firstSegment.sequence_number || 1,
          has_new_main_products: firstSegment.has_new_main_products || false,
          validity_service: firstSegment.validity_service || '',
          comfort_zone_code: firstSegment.comfort_zone_code || '',
          validity_start_date: firstSegment.validity_start_date || '',
          validity_end_date: firstSegment.validity_end_date || '',
          validity_intervals: firstSegment.validity_intervals || [],
          service_type: firstSegment.service_type || { name: '', code: '', modality: '' },
          client_ref: firstSegment.client_ref || '',
          is_international: firstSegment.is_international || false,
          service_identifier: firstSegment.service_identifier || '',
        }
      })

      const inboundSegment = computed(() => {
        if (!booking.value?.inbound_booking_tariff_segments?.length)
          return null
          
        const segments = booking.value.inbound_booking_tariff_segments
        const firstSegment = segments[0]
        const lastSegment = segments[segments.length - 1]
        
        return {
          ...firstSegment,
          segments: segments, 
          departure_station: {
            ...firstSegment.departure_station,
            connected_stations: firstSegment.departure_station.connected_stations || [],
            meta_data: firstSegment.departure_station.meta_data || [],
            timezone: firstSegment.departure_station.timezone || 'Europe/Oslo',
            country_code: firstSegment.departure_station.country_code || 'NOR',
            short_code: firstSegment.departure_station.short_code || '',
            name: firstSegment.departure_station.name || '',
            _u_i_c_station_code: firstSegment.departure_station._u_i_c_station_code || '',
          },
          arrival_station: {
            ...lastSegment.arrival_station,
            connected_stations: lastSegment.arrival_station.connected_stations || [],
            meta_data: lastSegment.arrival_station.meta_data || [],
            timezone: lastSegment.arrival_station.timezone || 'Europe/Oslo',
            country_code: lastSegment.arrival_station.country_code || 'NOR',
            short_code: lastSegment.arrival_station.short_code || '',
            name: lastSegment.arrival_station.name || '',
            _u_i_c_station_code: lastSegment.arrival_station._u_i_c_station_code || '',
          },
          booking_journey_segments: firstSegment.booking_journey_segments || [],
          required_products: firstSegment.required_products || [],
          additional_products: firstSegment.additional_products || [],
          fees: firstSegment.fees || [],
          travel_info: firstSegment.travel_info || [],
          travel_info_extended: firstSegment.travel_info_extended || [],
          validity_weekdays: firstSegment.validity_weekdays || '',
          tariff_group_code: firstSegment.tariff_group_code || '',
          uuid: firstSegment.uuid || '',
          travel_date: firstSegment.travel_date || '',
          sequence_number: firstSegment.sequence_number || 1,
          has_new_main_products: firstSegment.has_new_main_products || false,
          validity_service: firstSegment.validity_service || '',
          comfort_zone_code: firstSegment.comfort_zone_code || '',
          validity_start_date: firstSegment.validity_start_date || '',
          validity_end_date: firstSegment.validity_end_date || '',
          validity_intervals: firstSegment.validity_intervals || [],
          service_type: firstSegment.service_type || { name: '', code: '', modality: '' },
          client_ref: firstSegment.client_ref || '',
          is_international: firstSegment.is_international || false,
          service_identifier: firstSegment.service_identifier || '',
        }
      })

      const canRebookOutbound = computed(() => {
        return bookingStore.canRebookJourney('outbound')
      })

      const canRebookInbound = computed(() => {
        return bookingStore.canRebookJourney('inbound')
      })

      const canRebookEntire = computed(() => {
        return canRebookOutbound.value && canRebookInbound.value
      })

      const modalTitle = computed(() => {
        switch (currentDirection.value) {
          case 'outbound':
            return t('afterSale.rebook.modalTitles.outbound')
          case 'inbound':
            return t('afterSale.rebook.modalTitles.inbound')
          case 'entire':
            return t('afterSale.rebook.modalTitles.entire')
          default:
            return t('afterSale.rebook.title')
        }
      })

      const canRebook = computed(() => {
        const can = (() => {
          switch (currentDirection.value) {
            case 'outbound':
              return canRebookOutbound.value
            case 'inbound':
              return canRebookInbound.value
            case 'entire':
              return canRebookEntire.value
            default:
              return false
          }
        })()

        return can
      })

      const openModal = async (
        direction: 'outbound' | 'inbound' | 'entire'
      ) => {
        const bookingNumber = router.currentRoute.value.params
          .bookingNumber as string
        if (bookingNumber && !bookingStore.bookingData) {
          try {
            await bookingStore.fetchBookingData(bookingNumber)
          } catch (error) {
            console.error('Error fetching booking data:', error)
            errorMessage.value = 'Could not load booking data'
            return
          }
        }

        const bookingData = bookingStore.bookingData

        if (!bookingData?.booking_number) {
          console.error('No booking number available')
          errorMessage.value = 'Booking data not available'
          return
        }

        const segments =
          direction === 'inbound'
            ? bookingData.inbound_booking_tariff_segments
            : bookingData.outbound_booking_tariff_segments

        if (segments?.[0]) {
          const segment = segments[0]

          journeyStore.setOriginStation({
            _u_i_c_station_code: segment.departure_station._u_i_c_station_code,
            name: segment.departure_station.name,
            short_code: segment.departure_station.short_code,
            country_code: segment.departure_station.country_code,
          })
          journeyStore.setDestinationStation({
            _u_i_c_station_code: segment.arrival_station._u_i_c_station_code,
            name: segment.arrival_station.name,
            short_code: segment.arrival_station.short_code,
            country_code: segment.arrival_station.country_code,
          })

          const departureDate = new Date(segment.travel_date).toISOString()
          journeyStore.setDepartureDate(departureDate)

          if (
            direction === 'entire' &&
            bookingData.inbound_booking_tariff_segments?.[0]
          ) {
            const returnDate = new Date(
              bookingData.inbound_booking_tariff_segments[0].travel_date
            ).toISOString()
            journeyStore.setReturnDate(returnDate)
          }
        }

        currentDirection.value = direction
        showRebookModal.value = true
        errorMessage.value = ''
      }

      const closeModal = () => {
        showRebookModal.value = false
        journeyStore.$reset()
      }

      const handleSearch = () => {
        router.push({
          name: 'RebookingSearchResultPage',
          query: {
            rebookingType: currentDirection.value,
            bookingId: bookingStore.bookingData?.booking_id,
            entireJourney:
              currentDirection.value === 'entire' ? 'true' : 'false',
          },
        })
      }

      return {
        showRebookModal,
        errorMessage,
        modalTitle,
        currentDirection,
        canRebook,
        canRebookOutbound,
        canRebookInbound,
        canRebookEntire,
        openModal,
        closeModal,
        handleSearch,
        booking,
        outboundSegment,
        inboundSegment,
        isLoading,
        bookingStore,
        chevron,
        backLinkRoute,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .rebook-travel {
    padding: 2rem 0 7rem;

    &__header {
      margin-bottom: 2rem;
    }

    &__title {
      margin: 0;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__cards {
      display: flex;
      flex-direction: column;
      gap: 16px;

      @media (min-width: $breakpoint-md) {
        flex-direction: row;
      }
    }

    &__card {
      flex-basis: calc(100% / 3);
      display: flex;
      flex-direction: column;
      background: $clr-gray-60;
      border-radius: $border-radius;

      &-header {
        @include headline-small;
        flex-grow: 1;
        padding: rem(24) 24px;
        font-size: rem(18);
        background-color: white;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom: 1px solid $clr-primary-50;
      }

      &-cta {
        display: flex;
        justify-content: flex-end;
        padding: rem(24) 24px;

        .trailing__chevron {
          transform: rotate(90deg);
        }
      }

      &-button {
        @include body-large;
        display: inline-flex;
        align-items: center;
        text-decoration: none;
        color: $clr-primary;
      }
    }

    &__segment {
      flex-grow: 1;
      display: flex;
      height: 100%;
    }

    &__error {
      color: $clr-error;
      margin-bottom: 1rem;
    }

    &__loading {
      margin-bottom: 1rem;
    }
  }
</style>
